import {
	Alert,
	AsyncSelect,
	Badge,
	Button,
	Icons,
	Inline,
	RelativeTime,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SubmissionError,
	withServerErrors,
} from "@sembark-travel/ui-form"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, useLocationQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR, type IListResponse } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"
import { generatePath } from "../router-utils"
import {
	QuickSendUsage,
	WhatsAppQuickSendPreview,
} from "../WhatsAppMessages/conversation/QuickSendPreview"
import {
	QUICK_SEND_MESSAGE_TYPE_LABELS,
	WAQuickSendsXHR,
	type TWAQuickSend,
} from "./store"

type TFilters = TSearchParams

/**
 * The tenant's quick sends, listed from an integration's page. They belong to
 * no integration, so the list is the same from every one of them.
 */
export function WhatsAppQuickSendsList({
	integrationId,
}: {
	integrationId: number | string
}) {
	const [query, setQuery] = useLocationQuery<TFilters, TFilters>()
	const [searchParams, setSearchParams] = useSearch<TFilters>(query)
	// Bumped after a delete so the list is fetched again.
	const [version, setVersion] = useState(0)

	useEffect(() => {
		setQuery(searchParams)
	}, [searchParams, setQuery])

	const integration = { integrationId: String(integrationId) }

	return (
		<Search
			title="Quick Sends"
			initialParams={searchParams}
			onSearch={(params) => {
				setSearchParams({ ...params, page: 1 })
			}}
			actions={
				<ButtonLink
					to={generatePath(
						"/integrations/:integrationId/whatsapp/quick-sends/new",
						integration
					)}
					level="primary"
				>
					Create Quick Send
				</ButtonLink>
			}
		>
			{() => (
				<ListView<TWAQuickSend, TFilters>
					pageKey={`wa-quick-sends-${version}`}
					params={searchParams}
					onPageChange={(page: number) =>
						setSearchParams({ ...searchParams, page })
					}
					fetch={async (xhr, params) => {
						return xhr
							.get("/wa-quick-sends", { params })
							.then((resp) => resp.data)
					}}
				>
					{({ items }: { items: TWAQuickSend[] }) =>
						items.length === 0 ? (
							<Alert status="info">
								<Stack gap="2">
									<Text fontWeight="semibold">No Quick Sends Found</Text>
									<Text>
										A quick send is a saved set of messages, text and files, an
										agent sends into any WhatsApp conversation in one go. Create
										one for the answers your team gives most often.
									</Text>
								</Stack>
							</Alert>
						) : (
							<Table
								bordered
								striped
								responsive
								headers={["Name", "Messages", "Usage", "Updated", ""]}
								alignCols={{ 4: "right" }}
								rows={items.map((quickSend) => [
									<PreviewQuickSend key={quickSend.id} quickSend={quickSend} />,
									<Inline gap="1" flexWrap="wrap">
										{(quickSend.messages ?? []).map((message) => (
											<Badge key={message.id}>
												{QUICK_SEND_MESSAGE_TYPE_LABELS[message.type]}
											</Badge>
										))}
									</Inline>,
									<QuickSendUsage quickSend={quickSend} />,
									<RelativeTime timestamp={quickSend.updated_at} />,
									<Inline gap="1" justifyContent="end">
										<ButtonLink
											to={generatePath(
												"/integrations/:integrationId/whatsapp/quick-sends/:quickSendId/edit",
												{
													...integration,
													quickSendId: String(quickSend.id),
												}
											)}
											size="sm"
											level="tertiary"
											title="Edit Quick Send"
										>
											<Icons.Pencil />
										</ButtonLink>
										<DeleteQuickSend
											quickSend={quickSend}
											onDeleted={() => setVersion((current) => current + 1)}
										/>
									</Inline>,
								])}
							/>
						)
					}
				</ListView>
			)}
		</Search>
	)
}

/**
 * The name, which opens the messages as a conversation will show them.
 */
function PreviewQuickSend({ quickSend }: { quickSend: TWAQuickSend }) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			<Button inline level="tertiary" title="Preview Quick Send" onClick={open}>
				<Text as="span" fontWeight="semibold" color="accent">
					{quickSend.name}
				</Text>
			</Button>
			<Dialog open={isOpen} title={quickSend.name} onClose={close} sm>
				<Dialog.Body>
					<Stack gap="3">
						<QuickSendUsage quickSend={quickSend} />
						<WhatsAppQuickSendPreview quickSend={quickSend} />
					</Stack>
				</Dialog.Body>
				<Dialog.Footer>
					<Button onClick={close}>Close</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

function DeleteQuickSend({
	quickSend,
	onDeleted,
}: {
	quickSend: TWAQuickSend
	onDeleted: () => void
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()

	return (
		<>
			<Button
				size="sm"
				level="tertiary"
				status="danger"
				title="Delete Quick Send"
				onClick={open}
			>
				<Icons.Trash />
			</Button>
			<Dialog open={isOpen} title="Delete Quick Send" onClose={close} sm>
				<Form<unknown>
					onSubmit={withServerErrors(async () => {
						await WAQuickSendsXHR(xhr).remove(quickSend.id)
						showSnackbar(`"${quickSend.name}" deleted.`)
						close()
						onDeleted()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit} noValidate>
							<Dialog.Body>
								<Text>
									Agents will no longer find{" "}
									<Text as="span" fontWeight="semibold">
										{quickSend.name}
									</Text>{" "}
									in the composer, and its files are removed. Messages already
									sent with it stay in their conversations. This cannot be
									undone.
								</Text>
							</Dialog.Body>
							<Dialog.Footer>
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" status="danger" disabled={submitting}>
											{submitting ? "Deleting..." : "Delete"}
										</Button>
										<Button disabled={submitting} onClick={close}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

/**
 * Pick one of the tenant's quick sends, the ones agents reach for most
 * first. Each option says how many messages it sends.
 */
export function SelectWAQuickSend(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			{...props}
			labelKey="name"
			optionRenderer={({ option }: { option: TWAQuickSend }) => (
				<Stack gap="1">
					<Text>{option.name}</Text>
					{option.messages ? (
						<Text fontSize="sm" color="muted">
							{option.messages.length}{" "}
							{option.messages.length === 1 ? "message" : "messages"}
						</Text>
					) : null}
				</Stack>
			)}
			fetch={async (q: string) => {
				const resp = await xhr.get<IListResponse<TWAQuickSend>>(
					"/wa-quick-sends",
					{ params: { q: q || undefined, sort: "most_used", limit: 50 } }
				)
				return resp.data.data
			}}
		/>
	)
}
